import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api, formatApiError } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { Eye, EyeOff, User, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { t } = useLanguage();
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [infoBusy, setInfoBusy] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const [deletePw, setDeletePw] = useState("");
  const [showDeletePw, setShowDeletePw] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/auth?next=/profil", { replace: true }); return; }
    if (user) { setName(user.name || ""); setEmail(user.email || ""); }
  }, [user, navigate]);

  if (!user) return <div className="pt-40 pb-40 px-10 text-ink/40">{t.common.loading}</div>;

  const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-4 py-3 text-ink placeholder:text-ink/30 transition-colors duration-300 text-sm";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2";

  const saveInfo = async (e) => {
    e.preventDefault();
    setInfoBusy(true);
    try {
      const emailChanged = email.toLowerCase() !== user.email;
      await api.put("/me/profile", { name, email });
      await refreshUser();
      toast.success(emailChanged ? t.profile.savedEmailChanged : t.profile.saved);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInfoBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/me/change-password", { current_password: currentPw, new_password: newPw });
      toast.success(t.profile.passwordChanged);
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (!window.confirm(t.profile.deleteConfirm1)) return;
    if (!window.confirm(t.profile.deleteConfirm2)) return;
    setDeleteBusy(true);
    try {
      await api.delete("/me/account", { data: { password: deletePw } });
      await logout();
      toast.success(t.profile.deleted);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleteBusy(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-28 min-h-screen" data-testid="profile-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <FadeIn><Overline>{t.profile.overline}</Overline></FadeIn>
        <FadeIn delay={0.1}>
          <h1 className="mt-6 font-display font-semibold tracking-tighter text-4xl md:text-6xl text-ink">{t.profile.title}</h1>
        </FadeIn>

        <div className="mt-14 space-y-8">
          {/* PERSONAL INFO */}
          <FadeIn delay={0.15}>
            <form onSubmit={saveInfo} className="border border-ink/10 bg-surface p-8" data-testid="profile-info-form">
              <h2 className="font-display font-medium text-xl text-ink flex items-center gap-3"><User size={18} className="text-ember" /> {t.profile.infoTitle}</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t.auth.name}</label>
                  <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t.auth.email}</label>
                  <input data-testid="profile-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink/45">{t.profile.emailNote}</p>
              <button data-testid="profile-save-btn" disabled={infoBusy} className="btn-glow mt-6 bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-3.5 text-sm tracking-wide transition-colors duration-300">
                {infoBusy ? t.common.loading : t.profile.saveBtn}
              </button>
            </form>
          </FadeIn>

          {/* PASSWORD */}
          <FadeIn delay={0.2}>
            <form onSubmit={changePassword} className="border border-ink/10 bg-surface p-8" data-testid="profile-password-form">
              <h2 className="font-display font-medium text-xl text-ink flex items-center gap-3"><KeyRound size={18} className="text-ember" /> {t.profile.passwordTitle}</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t.profile.currentPassword}</label>
                  <div className="relative">
                    <input data-testid="profile-current-password" type={showPw ? "text" : "password"} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required className={`${inputCls} pr-12`} />
                    <button type="button" data-testid="profile-pw-toggle" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ember transition-colors" aria-label="toggle">
                      {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.auth.newPassword}</label>
                  <input data-testid="profile-new-password" type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} className={inputCls} />
                </div>
              </div>
              <p className="mt-3 text-xs text-ink/45">{t.profile.passwordNote}</p>
              <button data-testid="profile-password-btn" disabled={pwBusy} className="btn-glow mt-6 bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-3.5 text-sm tracking-wide transition-colors duration-300">
                {pwBusy ? t.common.loading : t.profile.passwordBtn}
              </button>
            </form>
          </FadeIn>

          {/* DANGER ZONE */}
          <FadeIn delay={0.25}>
            <div className="border border-red-200 bg-red-50/40 p-8" data-testid="profile-danger-zone">
              <h2 className="font-display font-medium text-xl text-red-600 flex items-center gap-3"><AlertTriangle size={18} /> {t.profile.dangerTitle}</h2>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed max-w-xl">{t.profile.dangerSub}</p>
              {user.role !== "admin" && (
                <form onSubmit={deleteAccount} className="mt-6 max-w-sm" data-testid="profile-delete-form">
                  <label className={labelCls}>{t.profile.deletePasswordLabel}</label>
                  <div className="relative">
                    <input data-testid="profile-delete-password" type={showDeletePw ? "text" : "password"} value={deletePw} onChange={(e) => setDeletePw(e.target.value)} required className={`${inputCls} pr-12`} />
                    <button type="button" data-testid="profile-delete-pw-toggle" onClick={() => setShowDeletePw(!showDeletePw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-red-500 transition-colors" aria-label="toggle">
                      {showDeletePw ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  <button data-testid="profile-delete-btn" disabled={deleteBusy} className="mt-4 border border-red-400 text-red-600 hover:bg-red-500 hover:text-white disabled:opacity-50 px-6 py-3.5 text-sm tracking-wide transition-colors duration-300">
                    {deleteBusy ? t.common.loading : t.profile.deleteBtn}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}
